import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { readCityChoice } from "../components/CityPickerModal";
import { api, errText } from "../lib/api";
import { useStore } from "../context/Store";
import { t, PATTERNS } from "../i18n";
import Countdown from "../components/Countdown";
import Seo, { JsonLd } from "../components/Seo";
import { trackViewItem, trackAddToCart, trackAddToWishlist, setCorridor } from "../components/MarketingLoader";
import ProductCard from "../components/ProductCard";
import ProductGallery from "../components/ProductGallery";
import ProductInfoSections from "../components/ProductInfoSections";
import { Check, Camera, Repeat, Heart } from "lucide-react";
import { DateField } from "../components/DateField";

// Small toggle: hearts the product for signed-in buyers, otherwise nudges to /account.
function WishlistToggle({ productId, locale, product }) {
  const [state, setState] = useState({ id: null, loading: true });
  useEffect(() => {
    if (!localStorage.getItem("fl_token")) { setState({ id: null, loading: false, guest: true }); return; }
    api.get("/account/wishlist", { params: { locale } })
      .then((r) => setState({ id: (r.data.find((x) => x.product_id === productId) || {}).id || null, loading: false }))
      .catch(() => setState({ id: null, loading: false, guest: true }));
  }, [productId, locale]);
  const toggle = async () => {
    if (state.guest) { window.location.href = `/${locale}/account`; return; }
    setState((s) => ({ ...s, loading: true }));
    try {
      if (state.id) {
        await api.delete(`/account/wishlist/${state.id}`);
        setState({ id: null, loading: false });
      } else {
        const { data } = await api.post("/account/wishlist", { product_id: productId });
        setState({ id: data.id, loading: false });
        if (product) trackAddToWishlist(product);
      }
    } catch (_) { setState((s) => ({ ...s, loading: false })); }
  };
  const active = !!state.id;
  return (
    <button onClick={toggle} disabled={state.loading} title={active ? t(locale, "savedWishlist") : t(locale, "saveWishlist")}
            data-testid="product-wishlist-toggle" data-active={active ? "true" : "false"}
            className={`p-2 border rounded-full transition-colors ${active ? "border-danger text-danger" : "border-brass-light text-muted hover:text-danger"}`}>
      <Heart size={18} fill={active ? "currentColor" : "none"} />
    </button>
  );
}

export default function Product() {
  const { locale = "en", slug } = useParams();
  const { currency, addGift, gifts, boot } = useStore();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [avail, setAvail] = useState(null);
  const [error, setError] = useState("");
  const [variant, setVariant] = useState(null);
  const [zoneId, setZoneId] = useState("");
  const [date, setDate] = useState("");
  const [win, setWin] = useState("afternoon");
  const [addons, setAddons] = useState({});
  const [message, setMessage] = useState("");
  const [cardLang, setCardLang] = useState(locale === "en" ? "fr" : locale);
  const [sender, setSender] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [alertEmail, setAlertEmail] = useState("");
  const [alertBusy, setAlertBusy] = useState(false);
  const [alertState, setAlertState] = useState(""); // "", "signed", "already", "error"
  const [alertError, setAlertError] = useState("");
  const [basketError, setBasketError] = useState("");
  const [productLoading, setProductLoading] = useState(true);

  useEffect(() => {
    let active = true;
    let loadedProduct;
    setProductLoading(true); setBasketError(""); setError("");
    window.scrollTo(0, 0);
    api
      .get(`/products/${slug}`, { params: { locale, currency, city: readCityChoice()?.slug || readCityChoice()?.key || undefined } })
      .then((r) => {
        if (!active) return;
        loadedProduct = r.data;
        setP(r.data);
        if (r.data.slug && r.data.slug !== slug) navigate(`/${locale}/${PATTERNS[locale].product}/${r.data.slug}`, { replace: true });
        setVariant(r.data.size_variants.find((v) => v.is_default) || r.data.size_variants[0]);
        setZoneId(r.data.zones?.[0]?.id || "");
        try { setCorridor({ recipient_city: r.data.city?.name }); trackViewItem(r.data); } catch (_) {}
        return api.get("/availability", { params: { city: r.data.city.slug } });
      })
      .then((r) => {
        if (!r || !active) return;
        setAvail(r.data);
        const next = r.data.dates?.find(d => d.available && !loadedProduct.blackout_dates?.includes(d.date));
        setDate(next?.date || "");
        if (!next) setBasketError("No delivery date is available for this bouquet right now.");
      })
      .catch((e) => { if (active) setError(errText(e)); })
      .finally(() => { if (active) setProductLoading(false); });
    return () => { active = false; };
  }, [slug, locale, currency, navigate]);

  // Keep the selected window valid: skip windows past cut-off (today) or blacked out by the florist for the chosen date.
  useEffect(() => {
    if (!p) return;
    const isToday = date === new Date().toISOString().slice(0, 10);
    const bw = (p.florist_blackout_windows || {})[date] || [];
    const isClosed = (w) => {
      if (bw.includes(w)) return true;
      if (isToday) { const s = p.same_day?.windows?.find((x) => x.key === w); return s ? !s.open : false; }
      return false;
    };
    if (isClosed(win)) {
      const open = ["morning", "afternoon", "evening"].find((w) => !isClosed(w));
      if (open) setWin(open);
    }
  }, [date, p, win]);

  const addonTotal = useMemo(
    () => Object.values(addons).reduce((s, a) => s + a.price * a.qty, 0),
    [addons]
  );
  const zone = p?.zones?.find((z) => z.id === zoneId);
  const total = (variant?.price || 0) + addonTotal + (zone?.fee || 0);

  if (error) return <div className="max-w-content mx-auto px-6 py-24" data-testid="product-error">{error}</div>;
  if (!p) return <div className="max-w-content mx-auto px-6 py-24 text-muted">{t(locale, "loading")}…</div>;

  const toggleAddon = (a) => {
    setAddons((prev) => {
      const next = { ...prev };
      if (next[a.id]) delete next[a.id];
      else next[a.id] = { id: a.id, name: a.name, price: a.price, qty: 1 };
      return next;
    });
  };

  const addToCart = (goToCheckout = true) => {
    if (productLoading || !variant || !zone || !date || !win || p.paused) {
      setBasketError("Please choose an available size, delivery neighbourhood and date.");
      return;
    }
    try { trackAddToCart({ ...p, currency, price: variant.price }); } catch (_) {}
    addGift({
      product_id: p.id,
      title: p.title,
      image: p.images[0],
      variant_name: variant.name,
      unit_price: variant.price,
      currency,
      symbol: p.symbol,
      city: { id: p.city.id, key: p.city.key, name: p.city.name },
      florist: p.florist || { shop_name: "Fleurinelle" },
      zone_id: zoneId,
      zone_name: zone?.name,
      zone_fee: zone?.fee || 0,
      delivery_date: date,
      delivery_window: win,
      blackout_windows: p.florist_blackout_windows || {},
      addons: Object.values(addons),
      card_message: message,
      card_language: cardLang,
      sender_name: sender,
      is_anonymous: anonymous,
      total,
    });
    if (goToCheckout) navigate(`/${locale}/checkout`);
  };

  const disabledDates = new Set((avail?.dates || []).filter((d) => !d.available).map((d) => d.date));

  return (
    <div className="max-w-content mx-auto px-6 py-10 lg:py-16" data-testid="product-page">
      <Seo
        title={p.meta_title || `${p.title} — ${p.city?.name} | Fleurinelle`}
        description={p.meta_description || p.description?.slice(0, 158)}
        image={p.images?.[0]}
        type="product"
        path={window.location.pathname}
      />
      <JsonLd data={p.json_ld} />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Fleurinelle", item: `${boot?.site_url || process.env.REACT_APP_BACKEND_URL}/${locale}` },
            ...(p.city ? [{ "@type": "ListItem", position: 2, name: p.city.name, item: `${boot?.site_url || process.env.REACT_APP_BACKEND_URL}/${locale}/${PATTERNS[locale].city}/${p.city.slug}` }] : []),
            { "@type": "ListItem", position: p.city ? 3 : 2, name: p.title, item: `${boot?.site_url || process.env.REACT_APP_BACKEND_URL}/${locale}/${PATTERNS[locale].product}/${p.slug}` },
          ],
        }}
      />
      <div className="flex flex-col gap-10 lg:grid lg:grid-cols-2 lg:gap-16 lg:items-start">
        {/* On phones these two flow around the buy box; on desktop they share the left column. */}
        <div className="contents lg:block lg:space-y-12">
          <div className="order-1 lg:order-none">
            <ProductGallery images={p.images || []} alt={p.title} />
          </div>
          <div className="order-3 lg:order-none">
            <ProductInfoSections locale={locale} product={p} cities={boot?.cities || []} />
          </div>
        </div>

        <div className="order-2 lg:order-none space-y-8">
          <div>
            <div className="flex flex-wrap gap-2 mb-3">
              {p.occasion_tags?.slice(0, 4).map((o) => (
                <span key={o.id} className="text-[11px] border border-petal text-petal px-2.5 py-1">{o.name}</span>
              ))}
            </div>
            <div className="flex items-start justify-between gap-3">
              <h1 className="font-display text-3xl lg:text-4xl text-ink flex-1">{p.title}</h1>
              <WishlistToggle productId={p.id} locale={locale} product={p} />
            </div>
            <p className="mt-2 text-sm text-muted">
              {p.type_label} · {p.city?.name} · Fleurinelle
            </p>
            <p className="mt-4 text-2xl text-ink tabular" data-testid="product-price">
              {p.symbol} {total}
            </p>
            <p className="text-xs text-muted mt-1">{t(locale, "deliveryFee")} {p.symbol} {zone?.fee ?? 0} · {t(locale, "included")}: {t(locale, "trustPhoto")}</p>
          </div>

          <div>
            <span className="smallcaps">{t(locale, "size")}</span>
            <div className="flex gap-3 mt-2">
              {p.size_variants.map((v) => (
                <button key={v.name} data-testid={`variant-${v.name}`} onClick={() => setVariant(v)} className={`border px-4 py-3 text-sm text-start ${variant?.name === v.name ? "border-ink bg-plaster-deep" : "border-brass-light"}`}>
                  <span className="block text-ink">{v.name}</span>
                  <span className="block text-xs text-muted tabular">{v.stem_count ? `${v.stem_count} ${t(locale, "stems")} · ` : ""}{p.symbol} {v.price}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="card p-6 space-y-4" data-testid="delivery-configurator">
            <h2 className="font-display text-xl text-ink">{t(locale, "deliveryDetails")}</h2>
            {(() => {
              const sd = p.same_day;
              const openWins = (sd?.windows || []).filter((w) => w.open);
              const lastOpen = openWins.reduce((a, b) => (a && a.seconds_remaining > b.seconds_remaining ? a : b), null);
              const legacyOpen = avail?.timing?.same_day_open;
              if ((sd ? openWins.length > 0 : legacyOpen)) {
                const secs = lastOpen ? lastOpen.seconds_remaining : avail?.timing?.seconds_remaining;
                return (
                  <div data-testid="same-day-notice">
                    <p className="text-xs text-brass">
                      {t(locale, "cutoffIn")} <Countdown seconds={secs} /> {t(locale, "forDeliveryToday")} {p.city?.name}
                    </p>
                    {openWins.length ? (
                      <p className="text-[11px] text-muted mt-0.5">{openWins.map((w) => t(locale, w.key).split(" ")[0]).join(" · ")}</p>
                    ) : null}
                  </div>
                );
              }
              return <p className="text-xs text-muted" data-testid="same-day-closed">{t(locale, "closedToday")}</p>;
            })()}
            <label className="block">
              <span className="smallcaps">{t(locale, "zone")}</span>
              <select data-testid="zone-select" className="field mt-1" value={zoneId} onChange={(e) => setZoneId(e.target.value)}>
                {p.zones?.map((z) => (
                  <option key={z.id} value={z.id}>{`${z.name} · ${p.symbol} ${z.fee}`}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="smallcaps">{t(locale, "date")}</span>
              <DateField
                testid="date-select"
                className="mt-1"
                value={date}
                min={avail?.next_available || new Date().toISOString().slice(0, 10)}
                onChange={(v) => {
                  if (disabledDates.has(v)) return;
                  setDate(v);
                }}
              />
            </label>
            <label className="block">
              <span className="smallcaps">{t(locale, "window")}</span>
              <select data-testid="window-select" className="field mt-1" value={win} onChange={(e) => setWin(e.target.value)}>
                {["morning", "afternoon", "evening"].map((w) => {
                  const isToday = date === new Date().toISOString().slice(0, 10);
                  const wStat = p.same_day?.windows?.find((x) => x.key === w);
                  const bwDate = (p.florist_blackout_windows || {})[date] || [];
                  const disabled = (isToday && wStat && !wStat.open) || bwDate.includes(w);
                  return <option key={w} value={w} disabled={disabled}>{`${t(locale, w)}${disabled ? ` — ${t(locale, "closed")}` : ""}`}</option>;
                })}
              </select>
            </label>
          </div>

          <div>
            <span className="smallcaps">{t(locale, "addOns")}</span>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
              {p.addons?.map((a) => (
                <button
                  key={a.id}
                  data-testid={`addon-${a.id}`}
                  disabled={a.always_included}
                  onClick={() => toggleAddon(a)}
                  className={`border p-3 text-start text-xs ${addons[a.id] ? "border-ink bg-plaster-deep" : "border-brass-light"} ${a.always_included ? "opacity-70" : ""}`}
                >
                  <img src={a.image_url} alt="" className="w-full h-16 object-cover mb-2" />
                  <span className="block text-ink">{a.name}</span>
                  <span className="text-muted tabular">{a.always_included ? t(locale, "included") : `${p.symbol} ${a.price}`}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <span className="smallcaps">{t(locale, "cardMessage")}</span>
            {(() => {
              // Suggestion chips + placeholder follow the handwriting language, not the page locale.
              const langMsgs = (p.card_messages_by_lang && p.card_messages_by_lang[cardLang]) || p.card_messages || [];
              const isRtl = cardLang === "ar";
              return (
                <>
                  <textarea
                    data-testid="card-message-input"
                    className="field h-24"
                    dir={isRtl ? "rtl" : "ltr"}
                    maxLength={320}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={langMsgs[0]}
                  />
                  <div className="flex justify-between text-xs text-muted">
                    <span>{message.length}/320</span>
                    <select data-testid="card-language-select" value={cardLang} onChange={(e) => setCardLang(e.target.value)} className="bg-transparent border border-brass-light px-2 py-1">
                      <option value="ar">العربية</option>
                      <option value="fr">Français</option>
                      <option value="en">English</option>
                      <option value="es">Español</option>
                    </select>
                  </div>
                  <div className="flex flex-wrap gap-2" dir={isRtl ? "rtl" : "ltr"} data-testid="card-suggestions">
                    {langMsgs.map((m) => (
                      <button key={m} type="button" onClick={() => setMessage(m)} className="text-[11px] border border-brass-light px-2 py-1 text-muted hover:border-brass">
                        {m.slice(0, 38)}…
                      </button>
                    ))}
                  </div>
                </>
              );
            })()}
            <input data-testid="sender-name-input" className="field" placeholder={t(locale, "senderName")} value={sender} onChange={(e) => setSender(e.target.value)} />
            <label className="flex items-center gap-2 text-sm text-muted">
              <input data-testid="anonymous-toggle" type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} />
              {t(locale, "anonymous")}
            </label>
          </div>

          {p.paused ? (
            <div className="border border-brass bg-plaster-deep p-4 text-sm text-ink space-y-3" data-testid="product-paused-notice">
              <div className="space-y-1">
                <p className="smallcaps text-brass">{t(locale, "currentlyUnavailable")}</p>
                <p className="text-charcoal">
                  {p.paused_until
                    ? t(locale, "backOnDate").replace("{date}", p.paused_until)
                    : t(locale, "backSoon")}
                </p>
              </div>
              {alertState === "signed" || alertState === "already" ? (
                <p className="text-sm text-success" data-testid="restock-alert-success">
                  {alertState === "already" ? t(locale, "restockAlreadyOn") : t(locale, "restockConfirm")}
                </p>
              ) : (
                <form
                  className="flex flex-col sm:flex-row gap-2 pt-1 border-t border-brass-light"
                  data-testid="restock-alert-form"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!alertEmail || alertBusy) return;
                    setAlertBusy(true);
                    setAlertError("");
                    try {
                      const r = await api.post(`/products/${slug}/restock-alert`, { email: alertEmail, locale });
                      setAlertState(r.data?.already_signed_up ? "already" : "signed");
                    } catch (err) {
                      setAlertError(errText(err));
                      setAlertState("error");
                    } finally {
                      setAlertBusy(false);
                    }
                  }}
                >
                  <label className="sr-only" htmlFor="restock-email">{t(locale, "buyerEmail")}</label>
                  <input
                    id="restock-email"
                    type="email"
                    required
                    data-testid="restock-alert-email"
                    className="field flex-1"
                    placeholder={t(locale, "restockEmailPlaceholder")}
                    value={alertEmail}
                    onChange={(e) => setAlertEmail(e.target.value)}
                  />
                  <button type="submit" data-testid="restock-alert-submit" className="btn-primary whitespace-nowrap" disabled={alertBusy || !alertEmail}>
                    {alertBusy ? "…" : t(locale, "restockAlertCta")}
                  </button>
                </form>
              )}
              {alertError ? <p className="text-danger text-xs" data-testid="restock-alert-error">{alertError}</p> : null}
              {alertState !== "signed" && alertState !== "already" ? (
                <p className="text-[11px] text-muted">{t(locale, "restockAlertHelp")}</p>
              ) : null}
            </div>
          ) : null}

          {basketError && <p role="alert" className="text-danger text-sm" data-testid="product-basket-error">{basketError}</p>}
          <button data-testid="add-to-cart-button" className="btn-primary w-full" onClick={() => addToCart(true)} disabled={productLoading || !variant || !zone || !date || !win || p.paused}>
            {p.paused ? t(locale, "currentlyUnavailable") : `${t(locale, "addToCart")} — ${p.symbol} ${total}`}
          </button>
          <button data-testid="add-another-gift-button" className="btn-ghost w-full" onClick={() => addToCart(false)} disabled={productLoading || !variant || !zone || !date || !win || p.paused}>
            {t(locale, "addAnotherRecipient")}
            {gifts.length ? ` (${gifts.length})` : ""}
          </button>

          <div className="space-y-1.5 text-xs text-charcoal" data-testid="product-ticks">
            {[t(locale, "tickFresh"), t(locale, "tickHandTied"), t(locale, "tickOnTime")].map((line) => (
              <p key={line} className="flex gap-2 items-start">
                <Check size={13} className="text-success shrink-0 mt-0.5" />{line}
              </p>
            ))}
          </div>

          <div className="space-y-3 text-xs text-muted border-t border-brass-light pt-6">
            <p className="flex gap-2"><Repeat size={14} className="text-brass shrink-0 mt-0.5" />{p.substitution_policy}</p>
            <p className="flex gap-2"><Camera size={14} className="text-brass shrink-0 mt-0.5" />{t(locale, "trustPhoto")}</p>
            <p className="flex gap-2"><Check size={14} className="text-brass shrink-0 mt-0.5" />{t(locale, "trustRefund")}</p>
          </div>
        </div>
      </div>

      {p.reviews?.length ? (
        <section className="mt-16 lg:mt-24 max-w-3xl" data-testid="product-reviews">
          <h2 className="font-display text-2xl text-ink mb-4">{t(locale, "reviews")}</h2>
          <div className="space-y-4">
            {p.reviews.map((r) => (
              <blockquote key={r.id} className="border-s-2 border-brass ps-4">
                <p className="smallcaps text-brass">{t(locale, "sentFrom")} {r.buyer_city || r.buyer_country} {t(locale, "to")} {r.delivery_city}</p>
                <p className="text-sm mt-1">{r.body}</p>
              </blockquote>
            ))}
          </div>
        </section>
      ) : null}

      <section className="mt-16">
        <h2 className="font-display text-2xl text-ink mb-8">{t(locale, "relatedProducts")}</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {p.related?.map((r, i) => (
            <ProductCard key={r.id} product={r} locale={locale} index={i} />
          ))}
        </div>
      </section>
    </div>
  );
}
